import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dashboardService from '../../services/DashboardService.js';
import SearchSubmitInput from './SearchSubmitInput.js';

const HeaderShipmentSearch = ({ onSelectShipment, variant = 'desktop' }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const requestRef = useRef(0);
  const isMobile = variant === 'mobile';

  useEffect(() => {
    const onClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const runSearch = async (raw) => {
    const q = String(raw || '').trim();
    setQuery(q);
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setOpen(true);
      const res = await dashboardService.searchShipments(q);
      if (requestRef.current !== requestId) return;
      setResults(res?.data || []);
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setResults([]);
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  };

  const goTo = (shipmentId) => {
    setOpen(false);
    setQuery('');
    if (onSelectShipment) onSelectShipment(shipmentId);
  };

  return (
    <form
      ref={wrapRef}
      onSubmit={(event) => event.preventDefault()}
      className={`relative ${isMobile ? 'w-full' : 'w-[180px] sm:w-[210px] md:w-[200px] lg:w-[280px] xl:w-[320px]'}`}
    >
      <SearchSubmitInput
        value={query}
        onSearch={runSearch}
        placeholder={t('dashboard.searchGcOrMobile')}
        ariaLabel={t('dashboard.searchGcOrMobile')}
        inputClassName="header-search-input w-full h-[42px] bg-gray-50 border border-gray-200 rounded-lg px-3 text-sm text-gray-800 focus:outline-none"
        inputProps={{
          type: 'search',
          title: t('dashboard.searchGcOrMobile'),
          'data-header-shipment-search': true,
        }}
      />
      {open && (
        <div className={`absolute ${isMobile ? 'left-0 right-0' : 'right-0'} mt-1 w-full min-w-0 bg-white border border-gray-200 rounded-xl shadow-lg z-[70] max-h-72 overflow-y-auto`}>
          {loading && <p className="px-3 py-2 text-sm text-gray-400">{t('common.loading')}</p>}
          {!loading && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-gray-400">{t('dashboard.noShipmentMatch')}</p>
          )}
          {results.map((row) => (
            <button
              key={row.shipment_id}
              type="button"
              onClick={() => goTo(row.shipment_id)}
              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 border-b border-gray-50 last:border-0"
            >
              <p className="text-sm font-medium text-blue-600 break-all">{row.shipment_id}</p>
              <p className="text-xs text-gray-500">
                {(row.from_hub_name || '-')} → {(row.to_hub_name || '-')} · {row.status || ''}
              </p>
            </button>
          ))}
        </div>
      )}
    </form>
  );
};

export default HeaderShipmentSearch;
